import { useMemo } from "react";

import { ErrorMessage } from "@/app/components/error-message";
import { LoadingScreen } from "@/components/loading-screen";
import { SearchBar } from "@/components/search-bar";
import { useTableControl } from "@/components/table-control";
import { OnboardingHeader } from "@/features/registration/components/onboarding-header";
import { useOrganisation } from "@/features/select-organisation/api/get-my-organisation";
import { useGetUsers } from "@/features/users/api/get-users";
import { AddInvitationModal } from "@/features/users/components/add-invitation-modal";
import { UserRowActions } from "@/features/users/components/user-row-actions";
import { UsersTable } from "@/features/users/components/users-table";
import { PublicUser } from "@classview/shared-types";
import { createColumnHelper } from "@tanstack/react-table";

export function UsersPage() {
  const columnHelper = createColumnHelper<PublicUser>();
  const { pagination, setPagination, search, setSearch } = useTableControl();
  const { organisation } = useOrganisation();

  const {
    data: userData,
    isLoading,
    isSuccess,
    isError,
    isFetching,
    isPlaceholderData,
    error,
  } = useGetUsers({
    pageSize: pagination.pageSize,
    pageIndex: pagination.pageIndex,
    search,
  });

  const columns = useMemo(
    () => [
      columnHelper.display({
        id: "name",
        cell: (info) => {
          return `${info.row.original.firstName} ${info.row.original.lastName}`;
        },
        header: () => <span>Name</span>,
        enableSorting: false,
      }),
      columnHelper.accessor("email", {
        id: "email",
        cell: (info) => <div className="truncate">{info.getValue()}</div>,
        header: () => <span>Email</span>,
        enableSorting: false,
      }),
      columnHelper.display({
        id: "actions",
        cell: (info) => (
          <UserRowActions
            userId={info.row.original.id}
            organisationId={organisation.id}
          />
        ),
        size: 70,
      }),
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  if (isLoading)
    return (
      <div>
        <OnboardingHeader
          title="Manage Users"
          description="View and manage users"
          className="mb-6"
        />
        <LoadingScreen />
      </div>
    );

  if (isError || !isSuccess)
    return (
      <div>
        <OnboardingHeader
          title="Manage Users"
          description="View and manage users"
          className="mb-6"
        />
        <ErrorMessage error={error} />
      </div>
    );

  return (
    <div>
      <OnboardingHeader
        title="Manage Users"
        description="View and manage users"
        className="mb-6"
      />
      <div className="mb-2 justify-between sm:flex">
        <SearchBar
          search={search}
          setSearch={setSearch}
          className="mb-2 sm:mb-0 sm:max-w-xs"
        />

        <AddInvitationModal organisationId={organisation.id} />
      </div>
      <UsersTable
        columns={columns}
        data={userData.items}
        pagination={pagination}
        pageCount={Math.ceil(userData.count / pagination.pageSize)}
        onPaginationChange={setPagination}
        isLoading={isFetching && isPlaceholderData}
      />
    </div>
  );
}
